import React, { useCallback, useEffect, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { SocialPost, SocialPostCollab } from 'types/campaign/collaborations'
import { formatNumber } from 'utils/helpers'

interface MediaSectionCarouselProps {
    title: string
    headerSlot?: React.ReactNode
    items: SocialPost[]
    loading?: boolean
    variant?: 'default' | 'large'
    onItemClick: (post: SocialPost) => void,
    showCollaborator?: boolean,
}

const MediaSectionCarousel: React.FC<MediaSectionCarouselProps> = ({
    title,
    headerSlot,
    items,
    loading = false,
    variant = 'default',
    onItemClick,
    showCollaborator = false,
}) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current
        if (!el) return

        setCanScrollLeft(el.scrollLeft > 0)
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
    }, [])

    useEffect(() => {
        updateScrollState()
    }, [items, loading, updateScrollState])

    const scroll = (direction: 'left' | 'right') => {
        const el = scrollRef.current
        if (!el) return

        const amount = variant === 'large' ? 182 : 155
        el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' })
    }

    if (!loading && items.length === 0) {
        return null
    }

    return (
        <section className="application-view-media__section">
            <h3 className="application-view-media__section-title">{title}</h3>
            {headerSlot ? <div className="application-view-media__section-header">{headerSlot}</div> : null}
            <div className="application-view-media__carousel">
                {canScrollLeft && (
                    <button
                        type="button"
                        className="application-view-media__nav application-view-media__nav--prev"
                        onClick={() => scroll('left')}
                        aria-label={`Scroll ${title} left`}
                    >
                        <i className="bx bx-chevron-left" />
                    </button>
                )}
                <div
                    ref={scrollRef}
                    className="application-view-media__track"
                    onScroll={updateScrollState}
                >
                    {loading && items.length === 0
                        ? Array.from({ length: 3 }).map((_, index) => (
                            <div
                                key={`skeleton-${index}`}
                                className={`application-view-media__card application-view-media__card--${variant}`}
                            >
                                <Skeleton className="application-view-media__card-skeleton" />
                            </div>
                        ))
                        : items.map((item) => (
                            <div
                                key={item.uuid}
                                className={`application-view-media__card-wrapper application-view-media__card-wrapper--${variant}`}
                            >
                                <button
                                    key={item.uuid}
                                    type="button"
                                    className={`application-view-media__card application-view-media__card--${variant}`}
                                    onClick={() => onItemClick(item)}
                                >
                                    <img
                                        src={item.thumbnail_url}
                                        alt=""
                                        className="application-view-media__card-img"
                                    />
                                    <div className="application-view-media__card-overlay">
                                        <span className="application-view-media__card-stat">
                                            <i className="mdi mdi-eye" aria-hidden />
                                            {formatNumber(item.views_count)}
                                        </span>
                                    </div>

                                </button>
                                {showCollaborator && item.collab?.handle ? (
                                    <div className="application-view-media__card-collab">
                                        <p className="application-view-media__card-collab-label">Collab with</p>
                                        <div className="application-view-media__card-collab-handle">
                                            <i
                                                className="ri-verified-badge-fill application-view-media__card-collab-verified"
                                                aria-hidden
                                            />
                                            <span
                                                className="application-view-media__card-collab-handle-text"
                                                title={`@${item.collab.handle}`}
                                            >
                                                @{item.collab.handle}
                                            </span>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ))}
                </div>
                {canScrollRight && (
                    <button
                        type="button"
                        className="application-view-media__nav application-view-media__nav--next"
                        onClick={() => scroll('right')}
                        aria-label={`Scroll ${title} right`}
                    >
                        <i className="bx bx-chevron-right" />
                    </button>
                )}
            </div>
        </section>
    )
}

export default MediaSectionCarousel
